/**
 * Campaign Detail — Admin Dashboard
 *
 * Tabbed detail page: Overview, Channels, Events, Analytics.
 * Channels tab shows expandable intent rows with short code / QR actions.
 *
 * API Endpoints:
 * - GET  /api/v1/admin/campaigns/:id                         — Campaign detail
 * - PUT  /api/v1/admin/campaigns/:id                         — Update campaign
 * - POST /api/v1/admin/campaigns/:id/archive                 — Archive campaign
 * - GET  /api/v1/admin/campaigns/:id/channels                — List channels
 * - GET  .../channels/:chid/intents                          — List intents
 * - POST .../intents/:iid/short-code                         — Create short code
 * - POST .../intents/:iid/qr-generate                        — Generate QR
 * - GET  .../intents/:iid/qr                                 — Download QR
 *
 * @story SCRUM-1936 — Campaign Admin Frontend
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import TabGroup from '../../components/common/TabGroup';
import Badge from '../../components/common/Badge';
import apiRequest from '../../services/api';
import QRCode from 'qrcode';
import { Copy, Check, ChevronDown, ChevronRight, ExternalLink, Download, Plus, Upload, X, Pencil } from 'lucide-react';
import QuestionResponseIntentDialog from '../../components/admin/QuestionResponseIntentDialog';
import ActionIntentDialog from '../../components/admin/ActionIntentDialog';
import SimpleIntentEditDialog from '../../components/admin/SimpleIntentEditDialog';
import NewEventModal from '../../components/admin/NewEventModal';

const styles = {
  container: { maxWidth: '1000px', margin: '0 auto', padding: '24px' },
  backLink: { display: 'inline-block', marginBottom: '16px', color: '#3b82f6', cursor: 'pointer', fontSize: '14px' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { fontSize: '22px', fontWeight: '600', margin: 0 },
  card: { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '20px' },
  fieldRow: { display: 'flex', gap: '16px', marginBottom: '12px', alignItems: 'center' },
  label: { fontWeight: '500', fontSize: '13px', color: '#6b7280', width: '140px', flexShrink: 0 },
  value: { fontSize: '14px', color: '#111827' },
  input: {
    padding: '6px 10px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '14px', flex: 1,
  },
  btn: {
    padding: '8px 16px', border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontWeight: '500', fontSize: '14px',
  },
  btnPrimary: { backgroundColor: '#3b82f6', color: '#fff' },
  btnSecondary: { backgroundColor: '#f3f4f6', color: '#374151' },
  btnDanger: { backgroundColor: '#ef4444', color: '#fff' },
  btnSmall: { padding: '4px 10px', fontSize: '12px' },
  channelRow: {
    border: '1px solid #e5e7eb', borderRadius: '8px',
    marginBottom: '12px', overflow: 'hidden',
  },
  channelHeader: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '12px 16px', backgroundColor: '#f9fafb', cursor: 'pointer',
  },
  intentRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '8px 16px 8px 40px', borderTop: '1px solid #f3f4f6', fontSize: '13px',
  },
  intentCode: { fontFamily: 'monospace', color: '#6b7280', fontSize: '12px' },
  placeholder: { textAlign: 'center', padding: '48px 0', color: '#6b7280' },
  copyBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#6b7280' },
};

const INTENT_TYPES = ['question_response', 'signup_request', 'rsvp', 'checkin', 'walk_in', 'event_rsvp', 'event_checkin', 'post_event_followup', 'calendar_booking'];
const EVENT_ONLY_INTENT_TYPES = new Set(['event_rsvp', 'event_checkin', 'rsvp', 'checkin', 'post_event_followup']);

function buildChannelTypeMaps(channelTypesData) {
  if (!channelTypesData) { return { labelMap: {}, utmMap: {}, intentMap: {}, eventSet: new Set(), legacySet: new Set() }; }
  const labelMap = {};
  const utmMap = {};
  const intentMap = {};
  const eventSet = new Set();
  const legacySet = new Set();
  for (const ct of channelTypesData) {
    labelMap[ct.value] = ct.label;
    utmMap[ct.value] = ct.utmDefaults;
    intentMap[ct.value] = ct.defaultIntents;
    if (ct.isEventType) { eventSet.add(ct.value); }
    if (ct.isLegacy) { legacySet.add(ct.value); }
  }
  return { labelMap, utmMap, intentMap, eventSet, legacySet };
}

function formatChannelTypeLabel(type, labelMap, legacySet) {
  let label;
  if (labelMap && labelMap[type]) { label = labelMap[type]; }
  else { label = type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()); }
  if (legacySet && legacySet.has(type)) { label += ' (legacy)'; }
  return label;
}

const STATUS_COLORS = {
  draft: 'gray',
  active: 'green',
  paused: 'yellow',
  completed: 'blue',
  archived: 'gray',
};

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [channels, setChannels] = useState([]);
  const [channelIntents, setChannelIntents] = useState({});
  const [expandedChannels, setExpandedChannels] = useState({});
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showCreateIntent, setShowCreateIntent] = useState(null);
  const [editingIntent, setEditingIntent] = useState(null);
  const [editIntentData, setEditIntentData] = useState(null);
  const [simpleEditIntent, setSimpleEditIntent] = useState(null);
  const [actionEditIntent, setActionEditIntent] = useState(null);
  const [actionEditData, setActionEditData] = useState(null);
  const [qrCapabilities, setQrCapabilities] = useState(null);
  const [channelTypesData, setChannelTypesData] = useState(null);
  const [channelTypesError, setChannelTypesError] = useState(null);
  const [toast, setToast] = useState(null);

  const channelTypeMaps = useMemo(() => buildChannelTypeMaps(channelTypesData), [channelTypesData]);

  const handleNavigate = (_navId, path) => {
    if (path) { navigate(path); }
  };

  const fetchCampaign = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest(`/api/v1/admin/campaigns/${id}`);
      if (res.success) {
        setCampaign(res.data);
        setEditForm({
          name: res.data.name || '',
          description: res.data.description || '',
          status: res.data.status || 'draft',
          starts_at: res.data.starts_at ? res.data.starts_at.slice(0, 10) : '',
          ends_at: res.data.ends_at ? res.data.ends_at.slice(0, 10) : '',
          hubspot_campaign_id: res.data.hubspot_campaign_id || '',
        });
      }
    } catch {
      // handled by apiRequest
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchChannels = useCallback(async () => {
    try {
      const res = await apiRequest(`/api/v1/admin/campaigns/${id}/channels`);
      if (res.success) {
        setChannels(res.data || []);
      }
    } catch {
      // handled by apiRequest
    }
  }, [id]);

  useEffect(() => { fetchCampaign(); }, [fetchCampaign]);

  useEffect(() => {
    apiRequest('/api/v1/admin/campaigns/qr-capabilities')
      .then((res) => { if (res.success) { setQrCapabilities(res.data); } })
      .catch(() => {});
    apiRequest('/api/v1/admin/campaigns/channel-types')
      .then((res) => { if (res.success) { setChannelTypesData(res.data); } })
      .catch(() => { setChannelTypesError('Failed to load channel types'); });
  }, []);
  useEffect(() => {
    if (activeTab === 'channels' || activeTab === 'events') {
      fetchChannels();
    }
  }, [activeTab, fetchChannels]);

  const fetchIntents = async (channelId) => {
    try {
      const res = await apiRequest(`/api/v1/admin/campaigns/${id}/channels/${channelId}/intents`);
      if (res.success) {
        setChannelIntents((prev) => ({ ...prev, [channelId]: res.data || [] }));
      }
    } catch {
      // handled
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const ACTION_INTENT_TYPES = ['signup_request', 'rsvp', 'checkin', 'walk_in', 'event_rsvp', 'event_checkin', 'calendar_booking'];

  const handleEditIntent = async (intent, channelId) => {
    if (intent.intent_type === 'question_response') {
      try {
        const res = await apiRequest(`/api/v1/admin/intent-codes/${intent.id}/config`);
        if (res.success) {
          setEditIntentData({ ...res.data, _channelId: channelId });
          setEditingIntent(intent);
        }
      } catch {
        showToast('Failed to load intent details', 'error');
      }
    } else if (ACTION_INTENT_TYPES.includes(intent.intent_type)) {
      try {
        const res = await apiRequest(`/api/v1/admin/intent-codes/${intent.id}/config`);
        if (res.success) {
          setActionEditData({ ...res.data, _channelId: channelId });
          setActionEditIntent(intent);
        }
      } catch {
        showToast('Failed to load intent details', 'error');
      }
    } else {
      setSimpleEditIntent({ ...intent, _channelId: channelId });
    }
  };

  const toggleChannel = (channelId) => {
    setExpandedChannels((prev) => {
      const next = { ...prev, [channelId]: !prev[channelId] };
      if (next[channelId] && !channelIntents[channelId]) {
        fetchIntents(channelId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiRequest(`/api/v1/admin/campaigns/${id}`, {
        method: 'PUT',
        body: {
          name: editForm.name,
          description: editForm.description || undefined,
          status: editForm.status,
          starts_at: editForm.starts_at || undefined,
          ends_at: editForm.ends_at || undefined,
          hubspot_campaign_id: editForm.hubspot_campaign_id || undefined,
        },
      });
      if (res.success) {
        setCampaign(res.data);
        setEditing(false);
      }
    } catch {
      // handled
    } finally {
      setSaving(false);
    }
  };

  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const handleArchive = async () => {
    setShowArchiveConfirm(false);
    try {
      const res = await apiRequest(`/api/v1/admin/campaigns/${id}/archive`, { method: 'POST' });
      if (res.success) {
        setCampaign(res.data);
      }
    } catch {
      // handled
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'channels', label: 'Channels', count: channels.length || undefined },
    { id: 'events', label: 'Events' },
    { id: 'analytics', label: 'Analytics' },
  ];

  if (loading) {
    return (
      <DashboardLayout
        userRole="admin" activeNavId="campaigns"
        onNavigate={handleNavigate}
        user={{ name: authUser?.fullName || 'Admin', email: authUser?.email || '', avatar: authUser?.profilePicture || null }}
      >
        <div style={styles.container}>
          <div style={styles.placeholder}>Loading campaign...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!campaign) {
    return (
      <DashboardLayout
        userRole="admin" activeNavId="campaigns"
        onNavigate={handleNavigate}
        user={{ name: authUser?.fullName || 'Admin', email: authUser?.email || '', avatar: authUser?.profilePicture || null }}
      >
        <div style={styles.container}>
          <div style={styles.placeholder}>Campaign not found.</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="campaigns"
      onNavigate={handleNavigate}
      user={{
        name: authUser?.fullName || authUser?.firstName || 'Admin',
        email: authUser?.email || '',
        avatar: authUser?.profilePicture || null
      }}
    >
      <div style={styles.container}>
        <span style={styles.backLink} onClick={() => navigate('/admin/marketing/campaigns')}>
          ← Back to Campaigns
        </span>

        <div style={styles.headerRow}>
          <h1 style={styles.title}>{campaign.name}</h1>
          <Badge color={STATUS_COLORS[campaign.status] || 'gray'}>{campaign.status}</Badge>
        </div>

        <TabGroup tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className="mb-6" />

        <div style={{ marginTop: '20px' }}>
          {activeTab === 'overview' && (
            <OverviewTab
              campaign={campaign}
              editing={editing}
              editForm={editForm}
              setEditForm={setEditForm}
              saving={saving}
              onEdit={() => setEditing(true)}
              onCancel={() => setEditing(false)}
              onSave={handleSave}
              onArchive={() => setShowArchiveConfirm(true)}
            />
          )}
          {activeTab === 'channels' && (
            <ChannelsTab
              campaignId={id}
              campaign={campaign}
              channels={channels}
              channelIntents={channelIntents}
              expandedChannels={expandedChannels}
              toggleChannel={toggleChannel}
              onRefreshIntents={fetchIntents}
              onAddChannel={() => setShowCreateChannel(true)}
              onAddIntent={(channelId) => setShowCreateIntent(channelId)}
              onEditIntent={handleEditIntent}
              qrCapabilities={qrCapabilities}
              showToast={showToast}
              labelMap={channelTypeMaps.labelMap}
              legacySet={channelTypeMaps.legacySet}
            />
          )}
          {activeTab === 'events' && (
            <EventsTab channels={channels} navigate={navigate} labelMap={channelTypeMaps.labelMap} legacySet={channelTypeMaps.legacySet} />
          )}
          {activeTab === 'analytics' && (
            <div style={styles.placeholder}>Campaign analytics coming soon.</div>
          )}
        </div>
      </div>

      {toast && (
        <div style={{
          position: 'fixed', top: '16px', right: '16px', padding: '12px 20px',
          borderRadius: '8px',
          backgroundColor: toast.type === 'error' ? '#fecaca' : toast.type === 'warning' ? '#fef3c7' : '#d1fae5',
          color: toast.type === 'error' ? '#dc2626' : toast.type === 'warning' ? '#d97706' : '#065f46',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1001, fontSize: '14px',
        }}>
          {toast.message}
        </div>
      )}

      {showCreateChannel && (
        <CreateChannelDialog
          campaignId={id}
          channelTypesData={channelTypesData}
          channelTypesError={channelTypesError}
          onClose={() => setShowCreateChannel(false)}
          onCreated={() => {
            setShowCreateChannel(false);
            fetchChannels();
            showToast('Channel created');
          }}
        />
      )}

      {showCreateIntent && (
        <CreateIntentDialog
          campaignId={id}
          channelId={showCreateIntent}
          channel={channels.find((c) => c.id === showCreateIntent)}
          campaign={campaign}
          channelTypeMaps={channelTypeMaps}
          onClose={() => setShowCreateIntent(null)}
          onCreated={() => {
            setShowCreateIntent(null);
            fetchIntents(showCreateIntent);
            showToast('Intent created');
          }}
        />
      )}

      {editingIntent && editIntentData && (
        <QuestionResponseIntentDialog
          existingIntent={editIntentData}
          onSubmit={async (payload) => {
            await apiRequest(`/api/v1/admin/intent-codes/${editingIntent.id}/config`, {
              method: 'PUT',
              body: payload,
            });
            fetchIntents(editIntentData._channelId);
          }}
          onCancel={() => { setEditingIntent(null); setEditIntentData(null); }}
          onToast={showToast}
        />
      )}

      {simpleEditIntent && (
        <SimpleIntentEditDialog
          intent={simpleEditIntent}
          onSubmit={async (payload) => {
            await apiRequest(`/api/v1/admin/intent-codes/${simpleEditIntent.id}`, {
              method: 'PATCH',
              body: payload,
            });
            fetchIntents(simpleEditIntent._channelId);
          }}
          onCancel={() => setSimpleEditIntent(null)}
          onToast={showToast}
        />
      )}

      {actionEditIntent && actionEditData && (
        <ActionIntentDialog
          intentType={actionEditIntent.intent_type}
          existingIntent={actionEditData}
          onSubmit={async (payload) => {
            await apiRequest(`/api/v1/admin/intent-codes/${actionEditIntent.id}/config`, {
              method: 'PUT',
              body: payload,
            });
            fetchIntents(actionEditData._channelId);
          }}
          onCancel={() => { setActionEditIntent(null); setActionEditData(null); }}
          onToast={showToast}
        />
      )}

      {showArchiveConfirm && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '400px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '600' }}>Archive Campaign</h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#6b7280' }}>
              Are you sure you want to archive this campaign? This action can be reversed.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowArchiveConfirm(false)}>
                Cancel
              </button>
              <button style={{ ...styles.btn, ...styles.btnDanger }} onClick={handleArchive}>
                Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function OverviewTab({ campaign, editing, editForm, setEditForm, saving, onEdit, onCancel, onSave, onArchive }) {
  const [copiedUtm, setCopiedUtm] = useState(false);

  const copyUtmCampaign = () => {
    navigator.clipboard.writeText(campaign.slug);
    setCopiedUtm(true);
    setTimeout(() => setCopiedUtm(false), 2000);
  };

  return (
    <div style={styles.card}>
      {editing ? (
        <>
          <div style={styles.fieldRow}>
            <span style={styles.label}>Name</span>
            <input
              style={styles.input}
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div style={styles.fieldRow}>
            <span style={styles.label}>Description</span>
            <textarea
              style={{ ...styles.input, minHeight: '60px', resize: 'vertical' }}
              value={editForm.description}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div style={styles.fieldRow}>
            <span style={styles.label}>Status</span>
            <select
              style={styles.input}
              value={editForm.status}
              onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div style={styles.fieldRow}>
            <span style={styles.label}>Start Date</span>
            <input
              type="date"
              style={styles.input}
              value={editForm.starts_at}
              onChange={(e) => setEditForm((f) => ({ ...f, starts_at: e.target.value }))}
            />
          </div>
          <div style={styles.fieldRow}>
            <span style={styles.label}>End Date</span>
            <input
              type="date"
              style={styles.input}
              value={editForm.ends_at}
              onChange={(e) => setEditForm((f) => ({ ...f, ends_at: e.target.value }))}
            />
          </div>
          <div style={styles.fieldRow}>
            <span style={styles.label}>HubSpot ID</span>
            <input
              style={styles.input}
              value={editForm.hubspot_campaign_id}
              onChange={(e) => setEditForm((f) => ({ ...f, hubspot_campaign_id: e.target.value }))}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={onSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={onCancel}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div style={styles.fieldRow}>
            <span style={styles.label}>Name</span>
            <span style={styles.value}>{campaign.name}</span>
          </div>
          {campaign.description && (
            <div style={styles.fieldRow}>
              <span style={styles.label}>Description</span>
              <span style={styles.value}>{campaign.description}</span>
            </div>
          )}
          <div style={styles.fieldRow}>
            <span style={styles.label}>Markets</span>
            <span style={styles.value}>
              {(campaign.markets || []).map((m) => m.market_name).join(', ') || '—'}
            </span>
          </div>
          <div style={styles.fieldRow}>
            <span style={styles.label}>Date Range</span>
            <span style={styles.value}>
              {campaign.starts_at ? new Date(campaign.starts_at).toLocaleDateString() : '—'}
              {' — '}
              {campaign.ends_at ? new Date(campaign.ends_at).toLocaleDateString() : '—'}
            </span>
          </div>
          <div style={styles.fieldRow}>
            <span style={styles.label}>utm_campaign</span>
            <span style={{ ...styles.value, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', fontSize: '13px' }}>
                {campaign.slug}
              </code>
              <button style={styles.copyBtn} onClick={copyUtmCampaign} title="Copy utm_campaign">
                {copiedUtm ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
              </button>
            </span>
          </div>
          {campaign.hubspot_campaign_id && (
            <div style={styles.fieldRow}>
              <span style={styles.label}>HubSpot Campaign</span>
              <span style={{ ...styles.value, display: 'flex', alignItems: 'center', gap: '4px' }}>
                {campaign.hubspot_campaign_id}
                <ExternalLink size={12} color="#6b7280" />
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={onEdit}>Edit</button>
            {campaign.status !== 'archived' && (
              <button style={{ ...styles.btn, ...styles.btnDanger }} onClick={onArchive}>Archive</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ChannelsTab({ campaignId, campaign, channels, channelIntents, expandedChannels, toggleChannel, onRefreshIntents, onAddChannel, onAddIntent, onEditIntent, qrCapabilities, showToast, labelMap, legacySet }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <button style={{ ...styles.btn, ...styles.btnPrimary, display: 'flex', alignItems: 'center', gap: '4px' }} onClick={onAddChannel}>
          <Plus size={14} /> New Channel
        </button>
      </div>

      {channels.length === 0 ? (
        <div style={styles.placeholder}>No channels yet. Add a channel to this campaign.</div>
      ) : (
        channels.map((ch) => (
          <div key={ch.id} style={styles.channelRow}>
            <div style={styles.channelHeader} onClick={() => toggleChannel(ch.id)}>
              {expandedChannels[ch.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <span style={{ fontWeight: '500', fontSize: '14px' }}>{ch.label || formatChannelTypeLabel(ch.channel_type, labelMap, legacySet)}</span>
              <Badge color="blue">{formatChannelTypeLabel(ch.channel_type, labelMap, legacySet)}</Badge>
              {ch.isUnknown && <Badge color="yellow">Unknown Type</Badge>}
              {ch.event_name && <span style={{ fontSize: '12px', color: '#6b7280' }}>({ch.event_name})</span>}
              {campaign?.markets?.length > 0 && (
                <Badge color="purple">{campaign.markets.map((m) => m.market_name).join(', ')}</Badge>
              )}
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#9ca3af' }}>
                {ch.intent_count || 0} intent{(ch.intent_count || 0) !== 1 ? 's' : ''}
              </span>
            </div>

            {expandedChannels[ch.id] && (
              <div>
                {(channelIntents[ch.id] || []).map((intent) => (
                  <IntentRow
                    key={intent.id}
                    intent={intent}
                    campaignId={campaignId}
                    channelId={ch.id}
                    campaignSlug={campaign?.slug}
                    channelLabel={ch.label || formatChannelTypeLabel(ch.channel_type, labelMap, legacySet)}
                    onRefresh={() => onRefreshIntents(ch.id)}
                    onEdit={() => onEditIntent(intent, ch.id)}
                    qrCapabilities={qrCapabilities}
                    showToast={showToast}
                  />
                ))}
                {channelIntents[ch.id] && channelIntents[ch.id].length === 0 && (
                  <div style={{ ...styles.intentRow, color: '#9ca3af' }}>No intents for this channel.</div>
                )}
                <div style={{ padding: '8px 16px 12px 40px' }}>
                  <button
                    style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSecondary, display: 'flex', alignItems: 'center', gap: '4px' }}
                    onClick={(e) => { e.stopPropagation(); onAddIntent(ch.id); }}
                  >
                    <Plus size={12} /> New Intent
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function QRPreview({ url, size = 40 }) {
  const [dataUrl, setDataUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { width: size, margin: 1, errorCorrectionLevel: 'M' })
      .then((d) => { if (!cancelled) { setDataUrl(d); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url, size]);
  if (!dataUrl) { return <div style={{ width: size, height: size, backgroundColor: '#f3f4f6', borderRadius: '4px' }} />; }
  return <img src={dataUrl} alt="QR" style={{ width: size, height: size, borderRadius: '4px' }} />;
}

function IntentRow({ intent, campaignId, channelId, campaignSlug, channelLabel, onRefresh, onEdit, qrCapabilities, showToast }) {
  const [expanded, setExpanded] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [creatingCode, setCreatingCode] = useState(false);
  const [generatingQr, setGeneratingQr] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [downloadingPng, setDownloadingPng] = useState(false);
  const [qrDetail, setQrDetail] = useState(null);

  const qrAvailable = qrCapabilities && qrCapabilities.generators && qrCapabilities.generators.length > 0;
  const intentType = intent.intent_type || 'intent';
  const fileBaseName = [campaignSlug, channelLabel, intentType].filter(Boolean).join('-').replace(/\s+/g, '_');
  const baseUrl = `${window.location.origin}/i/${intent.code}`;
  const cidToken = '{{contact.hs_object_id}}';
  const shortUrl = intent.short_code_value ? `odg1.pro/${intent.short_code_value}` : null;

  const copyText = (text, fieldKey) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const toggleExpand = async () => {
    const willExpand = !expanded;
    setExpanded(willExpand);
    if (willExpand && intent.intent_type === 'question_response' && !qrDetail) {
      try {
        const res = await apiRequest(`/api/v1/admin/intent-codes/${intent.id}/config`);
        if (res.success) { setQrDetail(res.data); }
      } catch {
        // non-blocking
      }
    }
  };

  const handleCreateShortCode = async () => {
    setCreatingCode(true);
    try {
      await apiRequest(
        `/api/v1/admin/campaigns/${campaignId}/channels/${channelId}/intents/${intent.id}/short-code`,
        { method: 'POST' }
      );
      onRefresh();
      if (showToast) { showToast('Short URL created'); }
      if (!intent.qr_code_id) {
        try {
          await handleGenerateQr();
        } catch {
          if (showToast) { showToast('Short code created but QR generation failed', 'warning'); }
        }
      }
    } catch {
      if (showToast) { showToast('Failed to create short URL', 'error'); }
    } finally {
      setCreatingCode(false);
    }
  };

  const handleGenerateQr = async () => {
    setGeneratingQr(true);
    try {
      await apiRequest(
        `/api/v1/admin/campaigns/${campaignId}/channels/${channelId}/intents/${intent.id}/qr-generate`,
        { method: 'POST' }
      );
      onRefresh();
      if (showToast) { showToast('QR code generated'); }
    } catch {
      if (showToast) { showToast('Failed to generate QR code', 'error'); }
    } finally {
      setGeneratingQr(false);
    }
  };

  const handleDownloadSvg = async () => {
    try {
      const VITE_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';
      const accessToken = localStorage.getItem('accessToken');
      const url = `${VITE_API_URL}/api/v1/admin/campaigns/${campaignId}/channels/${channelId}/intents/${intent.id}/qr?format=svg`;
      const res = await fetch(url, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!res.ok) { throw new Error(`Download failed: HTTP ${res.status}`); }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${fileBaseName}-qr.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      if (showToast) { showToast('SVG download failed', 'error'); }
    }
  };

  const handleDownloadPng = async () => {
    setDownloadingPng(true);
    try {
      const VITE_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';
      const accessToken = localStorage.getItem('accessToken');
      const url = `${VITE_API_URL}/api/v1/admin/campaigns/${campaignId}/channels/${channelId}/intents/${intent.id}/qr?format=png`;
      const res = await fetch(url, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!res.ok) { throw new Error(`Download failed: HTTP ${res.status}`); }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${fileBaseName}-qr.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      if (showToast) { showToast('PNG download failed', 'error'); }
    } finally {
      setDownloadingPng(false);
    }
  };

  const drawerRowStyle = {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '6px 16px 6px 64px', fontSize: '12px',
  };
  const drawerLabelStyle = { color: '#6b7280', fontWeight: 500, minWidth: '120px', flexShrink: 0 };
  const urlCodeStyle = { fontFamily: 'monospace', fontSize: '11px', color: '#374151', wordBreak: 'break-all', flex: 1 };

  const CopyBtn = ({ fieldKey, text }) => (
    <button style={styles.copyBtn} onClick={() => copyText(text, fieldKey)} title="Copy">
      {copiedField === fieldKey ? <Check size={12} color="#10b981" /> : <Copy size={12} />}
    </button>
  );

  const emailUrls = [];
  if (intent.intent_type === 'question_response' && qrDetail?.options?.length) {
    qrDetail.options.forEach((opt) => {
      emailUrls.push({
        label: `Answer ${opt.button_label}`,
        url: `${baseUrl}?v=${opt.response_value}&ref=${cidToken}`,
        key: `email-${opt.response_value}`,
      });
    });
  } else if (intent.intent_type === 'event_checkin') {
    emailUrls.push({
      label: 'Check-in URL (cookie-based)',
      url: baseUrl,
      key: 'email-checkin',
    });
  } else if (intent.intent_type === 'calendar_booking') {
    emailUrls.push({
      label: 'HubSpot Email URL',
      url: `${baseUrl}?cid=${cidToken}`,
      key: 'email-booking',
    });
  } else if (intent.intent_type !== 'question_response') {
    emailUrls.push({
      label: 'HubSpot Email URL',
      url: `${baseUrl}?ref=${cidToken}`,
      key: 'email-single',
    });
  }

  return (
    <>
      <div
        style={{
          ...styles.intentRow,
          cursor: 'pointer',
          backgroundColor: expanded ? '#f0fdf4' : undefined,
        }}
        onClick={toggleExpand}
      >
        {expanded ? <ChevronDown size={14} color="#6b7280" /> : <ChevronRight size={14} color="#6b7280" />}
        <span style={{ fontWeight: '500', fontSize: '13px', flex: 1 }}>
          {intent.label || intent.intent_type || 'Intent'}
        </span>
        <Badge color="gray">{(intent.intent_type || 'intent').replace(/_/g, ' ')}</Badge>
        <button style={styles.copyBtn} onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit intent">
          <Pencil size={13} />
        </button>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid #f3f4f6', backgroundColor: '#fafafa' }}>
          {/* Base URL */}
          <div style={drawerRowStyle}>
            <span style={drawerLabelStyle}>Base URL</span>
            <CopyBtn fieldKey="base" text={baseUrl} />
            <span style={urlCodeStyle}>{baseUrl}</span>
          </div>

          {/* HubSpot Email URL(s) */}
          {intent.intent_type === 'question_response' && !qrDetail && (
            <div style={{ ...drawerRowStyle, color: '#9ca3af' }}>
              <span style={drawerLabelStyle}>Email URLs</span>
              <span>Loading...</span>
            </div>
          )}
          {emailUrls.length === 1 && (
            <div style={drawerRowStyle}>
              <span style={drawerLabelStyle}>{emailUrls[0].label}</span>
              <CopyBtn fieldKey={emailUrls[0].key} text={emailUrls[0].url} />
              <span style={urlCodeStyle}>{emailUrls[0].url}</span>
            </div>
          )}
          {emailUrls.length > 1 && (
            <>
              <div style={{ ...drawerRowStyle, paddingBottom: '2px' }}>
                <span style={drawerLabelStyle}>Email URLs</span>
              </div>
              {emailUrls.map((eu) => (
                <div key={eu.key} style={{ ...drawerRowStyle, paddingLeft: '84px', paddingTop: '2px', paddingBottom: '2px' }}>
                  <span style={{ ...drawerLabelStyle, minWidth: '100px', fontSize: '11px' }}>{eu.label}</span>
                  <CopyBtn fieldKey={eu.key} text={eu.url} />
                  <span style={urlCodeStyle}>{eu.url}</span>
                </div>
              ))}
            </>
          )}

          {/* Short URL */}
          <div style={drawerRowStyle}>
            <span style={drawerLabelStyle}>Short URL</span>
            {shortUrl ? (
              <>
                <CopyBtn fieldKey="short" text={`https://${shortUrl}`} />
                <span style={{ ...urlCodeStyle, color: '#3b82f6' }}>https://{shortUrl}</span>
              </>
            ) : (
              <button
                style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSecondary }}
                onClick={(e) => { e.stopPropagation(); handleCreateShortCode(); }}
                disabled={creatingCode}
              >
                {creatingCode ? '...' : 'Create Short URL'}
              </button>
            )}
          </div>

          {/* QR Code */}
          <div style={drawerRowStyle}>
            <span style={drawerLabelStyle}>QR Code</span>
            {intent.qr_code_id ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <QRPreview url={shortUrl ? `https://${shortUrl}` : baseUrl} size={36} />
                <span style={{ color: '#10b981', fontSize: '12px', fontWeight: '600' }}>Generated</span>
                <button style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSecondary, display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={(e) => { e.stopPropagation(); handleDownloadSvg(); }}>
                  <Download size={11} /> SVG
                </button>
                <button style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSecondary, display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={(e) => { e.stopPropagation(); handleDownloadPng(); }} disabled={downloadingPng}>
                  <Download size={11} /> PNG
                </button>
              </span>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button
                  style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnPrimary }}
                  onClick={(e) => { e.stopPropagation(); handleGenerateQr(); }}
                  disabled={generatingQr || !qrAvailable}
                  title={!qrAvailable ? 'QR generation unavailable' : ''}
                >
                  {generatingQr ? '...' : 'Generate QR'}
                </button>
                <button
                  style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSecondary, display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={(e) => { e.stopPropagation(); setShowUpload(true); }}
                >
                  <Upload size={11} /> Upload
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {showUpload && (
        <QrUploadDialog
          campaignId={campaignId}
          channelId={channelId}
          intentId={intent.id}
          intentCode={intent.code}
          hasExistingQr={!!intent.qr_code_id}
          shortUrl={shortUrl ? `https://${shortUrl}` : baseUrl}
          onClose={() => setShowUpload(false)}
          onUploaded={() => {
            setShowUpload(false);
            onRefresh();
            if (showToast) { showToast('QR code uploaded'); }
          }}
        />
      )}
    </>
  );
}

function QrUploadDialog({ campaignId, channelId, intentId, intentCode: _intentCode, hasExistingQr, shortUrl, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const ALLOWED_TYPES = ['image/svg+xml', 'image/png'];

  const handleFile = (f) => {
    if (!ALLOWED_TYPES.includes(f.type)) {
      setError('Only SVG and PNG files are accepted');
      return;
    }
    setError(null);
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleUpload = async () => {
    if (!file) { return; }
    setUploading(true);
    setError(null);
    try {
      const VITE_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';
      const accessToken = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(
        `${VITE_API_URL}/api/v1/admin/campaigns/${campaignId}/channels/${channelId}/intents/${intentId}/qr-upload`,
        {
          method: 'POST',
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          body: formData,
        }
      );
      const json = await res.json();
      if (json.success) {
        onUploaded();
      } else {
        setError(json.error || 'Upload failed');
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '480px', maxHeight: '80vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Upload QR Code</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={18} color="#6b7280" />
          </button>
        </div>

        {shortUrl ? (
          <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#f0f9ff', borderRadius: '6px', fontSize: '13px' }}>
            <span style={{ color: '#6b7280' }}>Intent URL: </span>
            <code style={{ color: '#3b82f6' }}>{shortUrl}</code>
          </div>
        ) : (
          <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#fef9c3', borderRadius: '6px', fontSize: '13px', color: '#92400e' }}>
            No short URL created yet. Create a short URL first for tracking, or use the destination URL directly.
          </div>
        )}

        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
          {shortUrl
            ? 'Create your QR code using the URL above, then upload the file below. Only SVG and PNG files are accepted.'
            : 'Upload your QR code file below. Only SVG and PNG files are accepted.'}
        </p>

        {hasExistingQr && (
          <div style={{ padding: '8px 12px', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
            This will replace the existing QR code.
          </div>
        )}

        {error && (
          <div style={{ padding: '8px 12px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>
        )}

        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setDragOver(false)}
          style={{
            border: `2px dashed ${dragOver ? '#3b82f6' : '#d1d5db'}`,
            borderRadius: '8px',
            padding: '32px',
            textAlign: 'center',
            backgroundColor: dragOver ? '#eff6ff' : '#fafafa',
            cursor: 'pointer',
            marginBottom: '16px',
            transition: 'all 0.15s',
          }}
          onClick={() => document.getElementById(`qr-file-${intentId}`).click()}
        >
          <input
            id={`qr-file-${intentId}`}
            type="file"
            accept=".svg,.png,image/svg+xml,image/png"
            style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files[0]) { handleFile(e.target.files[0]); } }}
          />
          {preview ? (
            <div>
              <img src={preview} alt="QR preview" style={{ maxWidth: '200px', maxHeight: '200px', marginBottom: '8px' }} />
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{file.name}</p>
            </div>
          ) : (
            <div>
              <Upload size={32} color="#9ca3af" style={{ marginBottom: '8px' }} />
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                Drag and drop, or click to select
              </p>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9ca3af' }}>SVG or PNG only</p>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={onClose}>Cancel</button>
          <button
            style={{ ...styles.btn, ...styles.btnPrimary }}
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateChannelDialog({ campaignId, channelTypesData, channelTypesError, onClose, onCreated }) {
  const [channelType, setChannelType] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [eventId, setEventId] = useState('');
  const [events, setEvents] = useState([]);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [scaffoldedResult, setScaffoldedResult] = useState(null);

  const activeTypes = useMemo(() => (channelTypesData || []).filter((ct) => !ct.isLegacy), [channelTypesData]);
  const channelTypeMap = useMemo(() => {
    const m = {};
    for (const ct of (channelTypesData || [])) { m[ct.value] = ct; }
    return m;
  }, [channelTypesData]);
  const selectedType = channelTypeMap[channelType];
  const isEventType = selectedType ? selectedType.isEventType : false;

  useEffect(() => {
    if (isEventType && events.length === 0) {
      apiRequest('/api/v1/admin/events')
        .then((res) => { if (res.success) { setEvents(res.data || []); } })
        .catch(() => {});
    }
  }, [isEventType, events.length]);

  const handleSubmit = async () => {
    if (!label.trim()) { setError('Label is required'); return; }
    if (!channelType) { setError('Channel type is required'); return; }
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiRequest(`/api/v1/admin/campaigns/${campaignId}/channels`, {
        method: 'POST',
        body: {
          channel_type: channelType,
          label: label.trim(),
          description: description.trim() || undefined,
          event_id: isEventType && eventId ? eventId : undefined,
        },
      });
      if (res.success) {
        const scaffolded = selectedType ? (selectedType.defaultIntents || []) : [];
        if (scaffolded.length > 0) {
          setScaffoldedResult(scaffolded);
        } else {
          onCreated();
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to create channel');
    } finally {
      setSubmitting(false);
    }
  };

  if (scaffoldedResult) {
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '480px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '600', color: '#065f46' }}>Channel Created</h3>
          <p style={{ fontSize: '14px', color: '#374151', marginBottom: '12px' }}>
            The following intents were auto-created:
          </p>
          <ul data-testid="scaffolded-intents-list" style={{ margin: '0 0 16px', padding: '0 0 0 20px', fontSize: '13px', color: '#374151' }}>
            {scaffoldedResult.map((type) => (
              <li key={type} style={{ marginBottom: '4px' }}>
                <code style={{ backgroundColor: '#f3f4f6', padding: '1px 6px', borderRadius: '3px' }}>
                  {type}
                </code>
              </li>
            ))}
          </ul>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={onCreated}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '480px', maxHeight: '80vh', overflow: 'auto' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600' }}>New Channel</h3>

        {error && <div style={{ padding: '8px 12px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Channel Type *</label>
          <select style={styles.input} value={channelType} onChange={(e) => { setChannelType(e.target.value); const t = channelTypeMap[e.target.value]; if (!t || !t.isEventType) { setEventId(''); } }}>
            <option value="">Select type...</option>
            {channelTypesError && <option disabled>Error loading types</option>}
            {activeTypes.map((ct) => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Label *</label>
          <input style={styles.input} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g., Denver Meetup Banner" />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Description</label>
          <textarea style={{ ...styles.input, minHeight: '60px', resize: 'vertical' }} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" />
        </div>

        {isEventType && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Event</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <select style={{ ...styles.input, flex: 1 }} value={eventId} onChange={(e) => setEventId(e.target.value)}>
                <option value="">Select event...</option>
                {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
              </select>
              <button
                type="button"
                style={{ ...styles.btn, ...styles.btnSecondary, whiteSpace: 'nowrap' }}
                onClick={() => setShowNewEventModal(true)}
              >
                + New Event
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={onClose}>Cancel</button>
          <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Channel'}
          </button>
        </div>
      </div>

      {showNewEventModal && (
        <NewEventModal
          onClose={() => setShowNewEventModal(false)}
          onCreated={(newEvent) => {
            setEvents((prev) => [newEvent, ...prev.filter((e) => e.id !== newEvent.id)]);
            setEventId(newEvent.id);
            setShowNewEventModal(false);
          }}
        />
      )}
    </div>
  );
}

function CreateIntentDialog({ campaignId, channelId, channel, campaign, channelTypeMaps, onClose, onCreated }) {
  const fallbackUtm = { source: 'other', medium: 'other' };
  const utmDefaults = (channelTypeMaps.utmMap && channelTypeMaps.utmMap[channel?.channel_type]) || fallbackUtm;
  const [intentType, setIntentType] = useState('signup_request');
  const [label, setLabel] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmContent, setUtmContent] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [calendarMarketId, setCalendarMarketId] = useState('');
  const [calendarMarkets, setCalendarMarkets] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [_toast, setToast] = useState(null);
  const [availableIntentTypes, setAvailableIntentTypes] = useState(INTENT_TYPES);

  useEffect(() => {
    apiRequest('/api/v1/admin/intent-types?enabled=true')
      .then((res) => {
        if (res.success && res.data && res.data.length > 0) {
          setAvailableIntentTypes(res.data.map((t) => t.code.toLowerCase()));
        }
      })
      .catch(() => {});
  }, []);

  const filteredIntentTypes = useMemo(() => {
    if (channel?.event_id) { return availableIntentTypes; }
    return availableIntentTypes.filter((t) => !EVENT_ONLY_INTENT_TYPES.has(t));
  }, [availableIntentTypes, channel?.event_id]);

  useEffect(() => {
    if (!filteredIntentTypes.includes(intentType)) {
      setIntentType(filteredIntentTypes[0] || 'signup_request');
    }
  }, [filteredIntentTypes, intentType]);

  useEffect(() => {
    if (intentType === 'calendar_booking') {
      const markets = (campaign?.markets || []).map((m) => ({ id: m.market_id || m.id, name: m.name || m.market_name, slug: m.slug || '' }));
      setCalendarMarkets(markets);
      if (markets.length > 0 && !calendarMarketId) {
        setCalendarMarketId(markets[0].id);
      }
    }
  }, [intentType, campaign]);

  const handleQuestionResponseSubmit = async (payload) => {
    const result = await apiRequest('/api/v1/admin/intent-codes/question-response', {
      method: 'POST',
      body: JSON.stringify({ ...payload, campaign_id: campaignId, channel_id: channelId }),
      headers: { 'Content-Type': 'application/json' },
    });
    onCreated();
    return result;
  };

  const handleActionIntentSubmit = async (payload) => {
    const result = await apiRequest('/api/v1/admin/intent-codes/action-intent', {
      method: 'POST',
      body: JSON.stringify({ ...payload, campaign_id: campaignId, channel_id: channelId, event_id: channel?.event_id }),
      headers: { 'Content-Type': 'application/json' },
    });
    onCreated();
    return result;
  };

  const handleSubmit = async () => {
    if (!label.trim()) { setError('Label is required'); return; }
    if (intentType === 'calendar_booking' && (!campaign?.markets || campaign.markets.length === 0)) { setError('This campaign has no markets assigned. Add a market before creating calendar booking intents.'); return; }
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiRequest(`/api/v1/admin/campaigns/${campaignId}/channels/${channelId}/intents`, {
        method: 'POST',
        body: {
          intent_type: intentType,
          label: label.trim(),
          market_id: intentType === 'calendar_booking' ? (calendarMarketId || campaign?.markets?.[0]?.market_id || null) : undefined,
          max_uses: maxUses ? parseInt(maxUses, 10) : undefined,
          expires_at: expiresAt || undefined,
          utm_source: utmSource || undefined,
          utm_medium: utmMedium || undefined,
          utm_content: utmContent || undefined,
          utm_term: utmTerm || undefined,
        },
      });
      if (res.success) { onCreated(); }
    } catch (err) {
      setError(err.message || 'Failed to create intent');
    } finally {
      setSubmitting(false);
    }
  };

  const typeOptions = filteredIntentTypes.map((t) => ({ value: t, label: t.replace(/_/g, ' ').toUpperCase() }));
  const handleTypeChange = (newType) => setIntentType(newType);

  if (intentType === 'question_response') {
    return (
      <QuestionResponseIntentDialog
        intentType={intentType}
        availableTypes={typeOptions}
        onTypeChange={handleTypeChange}
        onSubmit={handleQuestionResponseSubmit}
        onCancel={onClose}
        onToast={setToast}
      />
    );
  }

  if (intentType === 'rsvp' || intentType === 'checkin' || intentType === 'walk_in' || intentType === 'signup_request' || intentType === 'event_rsvp' || intentType === 'event_checkin' || intentType === 'calendar_booking') {
    return (
      <ActionIntentDialog
        intentType={intentType}
        availableTypes={typeOptions}
        onTypeChange={handleTypeChange}
        onSubmit={handleActionIntentSubmit}
        onCancel={onClose}
        onToast={setToast}
      />
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '520px', maxHeight: '80vh', overflow: 'auto' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600' }}>New Intent</h3>

        {error && <div style={{ padding: '8px 12px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Intent Type *</label>
          <select style={styles.input} value={intentType} onChange={(e) => setIntentType(e.target.value)}>
            {filteredIntentTypes.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ').toUpperCase()}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Label *</label>
          <input style={styles.input} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g., Front door QR" />
        </div>

        {intentType === 'calendar_booking' && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Market</label>
            {calendarMarkets.length === 0 ? (
              <div data-testid="no-calendars-message" style={{ padding: '8px 12px', backgroundColor: '#eff6ff', color: '#1e40af', borderRadius: '6px', fontSize: '13px' }}>
                {"Market will be auto-assigned from the campaign. Connect a calendar in Scheduling settings to enable booking."}
              </div>
            ) : calendarMarkets.length === 1 ? (
              <div style={{ padding: '8px 12px', backgroundColor: '#f0fdf4', color: '#166534', borderRadius: '6px', fontSize: '13px' }}>
                {calendarMarkets[0].name}{calendarMarkets[0].slug ? ` (${calendarMarkets[0].slug})` : ''} — auto-selected
              </div>
            ) : (
              <select style={styles.input} value={calendarMarketId} onChange={(e) => setCalendarMarketId(e.target.value)}>
                {calendarMarkets.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}{m.slug ? ` (${m.slug})` : ''}</option>
                ))}
              </select>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Max Uses</label>
            <input style={styles.input} type="number" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="Unlimited" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Expires At</label>
            <input style={styles.input} type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </div>
        </div>

        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>UTM Parameters</div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Source</label>
              <input style={styles.input} value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder={utmDefaults.source} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Medium</label>
              <input style={styles.input} value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder={utmDefaults.medium} />
            </div>
          </div>

          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Campaign (read-only)</label>
            <input style={{ ...styles.input, backgroundColor: '#f3f4f6' }} value={campaign?.slug || ''} readOnly />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Content</label>
              <input style={styles.input} value={utmContent} onChange={(e) => setUtmContent(e.target.value)} placeholder="Optional" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Term</label>
              <input style={styles.input} value={utmTerm} onChange={(e) => setUtmTerm(e.target.value)} placeholder="Optional" />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={onClose}>Cancel</button>
          <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Intent'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EventsTab({ channels, navigate, labelMap, legacySet }) {
  const eventChannels = channels.filter((ch) => ch.event_id);

  if (eventChannels.length === 0) {
    return <div style={styles.placeholder}>No event-linked channels in this campaign.</div>;
  }

  return (
    <div style={styles.card}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead>
          <tr>
            <th style={{ ...styles.label, textAlign: 'left', padding: '8px 0' }}>Event</th>
            <th style={{ ...styles.label, textAlign: 'left', padding: '8px 0' }}>Channel</th>
            <th style={{ ...styles.label, textAlign: 'left', padding: '8px 0' }}>Type</th>
          </tr>
        </thead>
        <tbody>
          {eventChannels.map((ch) => (
            <tr key={ch.id} style={{ borderTop: '1px solid #f3f4f6' }}>
              <td style={{ padding: '8px 0' }}>
                <span
                  style={{ color: '#3b82f6', cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/marketing/events/${ch.event_id}`)}
                >
                  {ch.event_name || `Event ${ch.event_id}`}
                </span>
              </td>
              <td style={{ padding: '8px 0' }}>{ch.label || '—'}</td>
              <td style={{ padding: '8px 0' }}>
                <Badge color="blue">{formatChannelTypeLabel(ch.channel_type, labelMap, legacySet)}</Badge>
                {ch.isUnknown && <Badge color="yellow">Unknown Type</Badge>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
